import { FC, HTMLAttributes, startTransition, useRef } from 'react';

import { Conflict } from '@/types/Conflicten';
import { Defect } from '@/types/Defecten';
import { AtomicKnoopMaterieel, AtomicRBCMaterieelMap } from '@/views/ddp/state/types/VoorraadbeheerStore';
import { Reserve } from '@/views/ddp/types';
import { DDPNotitie } from '@/views/ddp/types/ddpTypes';

import usePlankTableStore from '../../../hooks/usePlankTableStore';

import styles from './SidepanelToggle.module.css';

/** Sidebar toggle opens the sidepanel from a conflict or a defect.
 *
 * @returns
 */
type ToggleType = HTMLAttributes<HTMLDivElement> & {
  eenheid?: string | number;
  conflict?: Conflict;
  defect?: Defect;
  reserve?: Reserve;
  notitie?: DDPNotitie;
  activeStyle?: boolean;
  isOnderhoud?: boolean;
  voorraad?: AtomicRBCMaterieelMap;
  voorraadRegionaal?: AtomicKnoopMaterieel;
};

/** Toggle the sidepanel and set an active state by adding CSS class '.active'.
 *
 * @param param0
 * @returns
 */
const SidepanelToggle: FC<ToggleType> = ({
  eenheid,
  children,
  conflict = undefined,
  defect = undefined,
  reserve = undefined,
  notitie = undefined,
  voorraad = undefined,
  voorraadRegionaal = undefined,
  activeStyle = true,
  isOnderhoud = undefined,
  ...divProps
}) => {
  const setEenheid = usePlankTableStore((state) => state.setEenheid);
  const setSidepanelVoorraad = usePlankTableStore((state) => state.setSidepanelVoorraad);
  const setSidepanelVoorraadRegionaal = usePlankTableStore((state) => state.setSidepanelVoorraadRegionaal);
  const ref = useRef<HTMLDivElement>(null);
  const isActive = usePlankTableStore(
    (state) =>
      activeStyle &&
      ((eenheid && state.isEenheidSelected({ eenheid, defect, conflict, reserve, isOnderhoud })) ||
        ((voorraad || voorraadRegionaal) && state.isVoorraadSelected({ voorraad, voorraadRegionaal }))),
  );
  const onClick = () => {
    startTransition(() => {
      if (eenheid && (defect || conflict || reserve || notitie))
        void setEenheid({ eenheid, defect, conflict, reserve, notitie, isOnderhoud });
      if (voorraad) void setSidepanelVoorraad(voorraad);
      if (voorraadRegionaal) void setSidepanelVoorraadRegionaal(voorraadRegionaal);
    });
    setTimeout(() => {
      document.getElementById('ddp-sidepanel')?.focus();
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'end' });
    }, 500); // Must equal longest transition.
  };

  const buttonClass = activeStyle ? styles.spToggleStyleButton : styles.spToggleStyleButtonNoActive;

  return (
    <div
      ref={ref}
      {...divProps}
      className={`${buttonClass} ${isActive ? styles.active : ''} ${divProps?.className ? ` ${divProps.className}` : ''}`}
    >
      <a
        role='button'
        onClick={onClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onClick();
        }}
        tabIndex={1}
      >
        {children}
      </a>
    </div>
  );
};
export default SidepanelToggle;
